import type { Chart, Plugin } from "chart.js";

/*
 * Schutz gegen das Zeichnen vor dem ersten Update in
 * `chartjs-plugin-datalabels`.
 *
 *     Uncaught (in promise) TypeError:
 *       Cannot read properties of undefined (reading 'length')
 *       chartjs-plugin-datalabels.esm.js:1003
 *
 * Das Plugin legt in `beforeInit` nur `chart.$datalabels = { _actives: [] }`
 * an. `_labels` entsteht erst in `beforeUpdate` (Zeile 1236 des ESM-Builds)
 * und wird in `afterUpdate` gefüllt (1295). `afterDatasetsDraw` (1301) reicht
 * genau dieses `_labels` an `layout.draw(chart, labels)` weiter, und dort
 * bricht `labels.length` ab, wenn noch nie ein Update lief.
 *
 * Dass es dazu kommt, liegt an Chart.js 4 selbst — nachgelesen in
 * `node_modules/chart.js/dist/chart.js`:
 *
 *     // bindResponsiveEvents(), 6308
 *     const attached = () => { ...; this.attached = true; this.resize(); ... }
 *     // _resize(), 5775
 *     if (this.attached) { if (this._doResize(mode)) { this.render(); } }
 *     // Konstruktor, 5696
 *     this._initialize();                       // hierin läuft das Obige
 *     if (this.attached) { this.update(); }     // erst hier entsteht _labels
 *
 * Hängt der Canvas beim Bau schon am Dokument, meldet die Plattform sofort
 * „attach", und Chart.js zeichnet **aus `_initialize()` heraus**, bevor
 * `update()` je gelaufen ist.
 *
 * Wichtig, weil die frühere Notiz im VALERI-Code das Gegenteil behauptete:
 * Der Absturz braucht einen **eingehängten** Canvas, nicht einen losgelösten.
 * Ein losgelöster Canvas meldet kein „attach", `this.attached` bleibt falsch,
 * und `_resize` zeichnet gar nicht erst. Das `v-if="bereit"` in
 * `LineChart.vue` stellt den Canvas gerade sicher ins Dokument und stellt
 * damit genau die Bedingung her, unter der es knallt — es verhindert sie
 * nicht.
 *
 * Deshalb hier eine Absicherung, die nicht von der Reihenfolge der
 * Plugin-Registrierung abhängt: `beforeDatasetsDraw` läuft in jedem
 * `draw()`-Durchgang vor `afterDatasetsDraw` aller Plugins. Fehlt `_labels`,
 * wird es auf eine leere Liste gesetzt — der verfrühte Durchgang zeichnet
 * dann nichts, und das unmittelbar folgende `update()` füllt die Liste
 * regulär. An der Darstellung ändert sich nichts.
 */
/**
 * Der Zwischenstand, den `chartjs-plugin-datalabels` am Diagramm ablegt.
 *
 * `_labels` fehlt zwischen `beforeInit` und dem ersten `beforeUpdate` — genau
 * das Fenster, um das es hier geht. Das Plugin bringt für `chart.$datalabels`
 * keine Typen mit, deshalb hier das Nötigste von Hand.
 */
interface DatalabelsZustand {
  _actives?: unknown[];
  _labels?: unknown[];
}

export const datalabelsGuard: Plugin = {
  id: "datalabelsGuard",
  beforeDatasetsDraw(chart: Chart) {
    const zustand = (chart as Chart & { $datalabels?: DatalabelsZustand }).$datalabels;
    if (zustand && !Array.isArray(zustand._labels)) {
      zustand._labels = [];
    }
  },
};

export default datalabelsGuard;
